import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, inr, telHref } from "@/lib/format";
import useSeo from "@/hooks/useSeo";
import { useConfig } from "@/hooks/useConfig";
import StatusBadge from "@/components/StatusBadge";
import PaymentPanel from "@/components/booking/PaymentPanel";
import CancelPanel, { CancelRequested } from "@/components/booking/CancelPanel";

const Item = ({ k, v, testId }) => (
  <div className="py-3 flex justify-between gap-4 border-b border-[#E2DCD0] text-sm">
    <span className="text-[#6b6b6b]">{k}</span><span className="text-right font-medium" data-testid={testId}>{v}</span>
  </div>
);

const Headline = ({ b }) => {
  if (b.payment_status === "paid") return <><CheckCircle2 className="text-emerald-700" size={36} /><h1 className="text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight">Booking confirmed</h1></>;
  if (b.payment_status === "pending_verification") return <><Clock className="text-sky-700" size={36} /><h1 className="text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight">Payment under verification</h1></>;
  if (b.payment_status === "awaiting_quote") return <><Clock className="text-[#C28B5E]" size={36} /><h1 className="text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight">Booking request received</h1></>;
  return <h1 className="text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight">{b.status === "cancelled" ? "Booking cancelled" : b.payment_status === "expired" ? "Booking hold expired" : "Complete your payment"}</h1>;
};

export default function BookingStatus() {
  useSeo("Your Booking", "Booking details and payment status.");
  const { id } = useParams();
  const cfg = useConfig();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [b, setB] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${id}`, { params: { token } }).then((r) => setB(r.data)).catch(() => setError(true));
  }, [id, token]);

  if (error) return <div className="container-x py-24 text-center"><h1 className="text-4xl text-[#1E3B2E]">Booking not found</h1><Link to="/manage-booking" className="btn-primary mt-8" data-testid="booking-notfound-manage-link">Find my booking</Link></div>;
  if (!b) return <div className="min-h-[60vh]" />;
  const canPay = b.status !== "cancelled" && ["pending_payment", "failed"].includes(b.payment_status);

  return (
    <div className="bg-[#F4EFE6] min-h-screen py-10 sm:py-16">
      <div className="container-x max-w-4xl space-y-6">
        <div className="flex flex-col gap-3">
          <Headline b={b} />
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm">Booking ID <strong className="tracking-wider" data-testid="booking-id">{b.booking_id}</strong></span>
            <StatusBadge status={b.payment_status} testId="booking-payment-status" />
            {b.status !== "pending" && <StatusBadge status={b.status} testId="booking-status" />}
          </div>
          {b.payment_status === "awaiting_quote" && <p className="text-sm text-[#3b3b3b]" data-testid="booking-quote-note">Our team will confirm your room rate shortly by email. You can then pay securely via UPI from this page. No payment is needed now.</p>}
          {b.payment_status === "pending_verification" && <p className="text-sm text-[#3b3b3b]">UTR <strong data-testid="booking-utr">{b.utr}</strong> received. Our team verifies payments promptly; you'll get a confirmation email once verified.</p>}
          {b.payment_status === "expired" && <p className="text-sm text-[#3b3b3b]">{b.quote_required && b.total == null ? "We're sorry, we couldn't confirm a rate for this request in time. Please contact us or " : "The 24-hour hold lapsed without payment. Please "}<Link to="/book" className="underline">make a new booking</Link>.</p>}
        </div>
        {canPay && <PaymentPanel booking={b} token={token} onUpdated={setB} />}
        <section className="bg-white border border-[#E2DCD0] p-6 sm:p-8" data-testid="booking-details">
          <div className="flex justify-between items-start gap-4">
            <div><p className="eyebrow text-[#C28B5E]">Booking details</p><h2 className="mt-1 text-2xl text-[#1E3B2E]">{b.property_name}</h2><p className="text-xs text-[#6b6b6b] mt-1">{b.property_address}</p></div>
            <button onClick={() => window.print()} className="no-print text-[#1E3B2E] flex items-center gap-2 text-xs uppercase tracking-[0.15em]" data-testid="booking-print-btn"><Printer size={15} />Print</button>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-x-10">
            <div>
              <Item k="Guest" v={b.guest.name} testId="booking-guest-name" />
              <Item k="Email" v={b.guest.email} testId="booking-guest-email" />
              <Item k="Contact" v={b.guest.phone} testId="booking-guest-phone" />
              <Item k="Guests" v={`${b.adults} adult(s), ${b.children} child(ren)`} testId="booking-guests" />
            </div>
            <div>
              <Item k="Check-in" v={fmtDate(b.check_in)} testId="booking-checkin" />
              <Item k="Check-out" v={fmtDate(b.check_out)} testId="booking-checkout" />
              <Item k="Nights" v={b.nights} testId="booking-nights-count" />
              <Item k="Rooms" v={b.total_rooms} testId="booking-total-rooms" />
            </div>
          </div>
          <div className="mt-8">
            {b.rooms.map((r) => <Item key={r.slug} k={`${r.name} × ${r.qty}${r.price ? ` (${inr(r.price)}/night)` : ""}`} v={r.subtotal == null ? (b.total == null ? "Rate on request" : "Included in confirmed rate") : b.quote_required && b.total != null ? "Included in confirmed rate" : inr(r.subtotal)} testId={`booking-room-${r.slug}`} />)}
            <Item k="GST" v="Included" /><Item k="Breakfast" v="Included" /><Item k="Extra bed" v="Complimentary" />
            {b.discount > 0 && <Item k={b.discount_label || "Discount"} v={`− ${inr(b.discount)}`} testId="booking-discount" />}
            <div className="pt-4 flex justify-between items-end"><span className="eyebrow text-[#1E3B2E]">Total</span><span className="font-['Cormorant_Garamond'] text-3xl text-[#1E3B2E]" data-testid="booking-total">{b.total == null ? "To be confirmed" : inr(b.total)}</span></div>
          </div>
          <p className="mt-6 text-xs text-[#3b3b3b]" data-testid="booking-status-checkin-info">Check-in {cfg.checkin_time || "15:00 hrs"} · Check-out {cfg.checkout_time || "11:00 hrs"}{cfg.booking_note ? `. ${cfg.booking_note}` : ""}</p>
          <p className="mt-2 text-xs text-[#6b6b6b]">Questions? Call <a className="underline" href={telHref(b.property_phone)}>{b.property_phone}</a>. Keep your booking ID handy. You can also find this booking any time via <Link to="/manage-booking" className="underline">Manage Booking</Link>.</p>
        </section>
        {b.cancel_request && b.status !== "cancelled" && <CancelRequested req={b.cancel_request} />}
        {b.cancel_preview?.allowed && <CancelPanel booking={b} token={token} onUpdated={setB} />}
      </div>
    </div>
  );
}
